import React, { FC, useMemo } from "react";
import Typography from "../../Unknown/Typography";
import { ConfirmedAppointment } from "../AppointmentConfirmedScreen/parseConfirmedAppointment";
import useTranslations from "./useTranslations";
import SuggestedAddOnItem from "./SuggestedAddOnItem";
import Grid from "../../Unknown/Grid";
import {
  AddOnSelectionHandlerProps,
  SuggestedAddOn,
} from "./useSuggestedAddOns";
import { SuggestedPackage } from "./useSuggestedPackages";
import { BookingPackage } from "../AppointmentBookingContext/useBookingPackages";

type SuggestedAddOnListProps = {
  appointmentData: ConfirmedAppointment;
  suggestedAddOns: SuggestedAddOn[];
  addedPackages?: SuggestedPackage[];
  centerPackages: BookingPackage[];
  selectedPackageIds: Record<string, string[]>;
  selectedOfferingIds: Record<string, string[]>;
  error: Record<string, string | null>;
  setSelectedPackageIds: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  setSelectedOfferingIds: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  setError: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  onSelect: (props: AddOnSelectionHandlerProps) => void;
  onDeselect: (addOnId: string) => void;
  onQuantityChange: (addOnId: string, quantity: number) => void;
};

const SuggestedAddOnList: FC<SuggestedAddOnListProps> = ({
  appointmentData,
  suggestedAddOns,
  addedPackages = [],
  centerPackages,
  selectedOfferingIds,
  setSelectedOfferingIds,
  selectedPackageIds,
  setSelectedPackageIds,
  error,
  setError,
  onSelect,
  onDeselect,
  onQuantityChange,
}) => {
  const {
    translations: { suggestedAddOnsTitle },
  } = useTranslations();

  const sortedAddOns = useMemo(() => {
    return suggestedAddOns.sort((a, b) => {
      if (
        a.centerAddOn.appliesTo === "study" &&
        b.centerAddOn.appliesTo === "appointment"
      ) {
        return 1;
      }

      if (
        a.centerAddOn.appliesTo === "appointment" &&
        b.centerAddOn.appliesTo === "study"
      ) {
        return -1;
      }

      return 0;
    });
  }, [suggestedAddOns]);

  return (
    <Grid item xs={12}>
      <Typography variant="h3">{suggestedAddOnsTitle}</Typography>
      {sortedAddOns.map((suggestedAddOn) => {
        const { centerAddOn, isSelected } = suggestedAddOn;
        const onSelectAddOn = (params: {
          selectedPackageIds: string[];
          selectedOfferingIds: string[];
        }) =>
          onSelect({
            addOnId: centerAddOn.id,
            selectedPackageIds: params.selectedPackageIds,
            selectedOfferingIds: params.selectedOfferingIds,
          });

        const onDeselectAddOn = () => onDeselect(centerAddOn.id);
        const onAddOnQuantityChange = (quantity: number) =>
          onQuantityChange(centerAddOn.id, quantity);

        return (
          <SuggestedAddOnItem
            key={centerAddOn.id}
            addOnId={centerAddOn.id}
            addOnName={centerAddOn.name}
            addOnPrice={centerAddOn.price}
            calculatedAddOnPrice={suggestedAddOn.price}
            marketingText={centerAddOn.marketingText || ""}
            isSelected={isSelected}
            appliesTo={centerAddOn.appliesTo}
            appliedPrice={suggestedAddOn.appliedPrice}
            allowMultiplePurchases={centerAddOn.allowMultiplePurchases}
            quantity={suggestedAddOn.quantity}
            appointmentData={appointmentData}
            selectedPackages={suggestedAddOn.selectedPackages}
            selectedOfferings={suggestedAddOn.selectedOfferings}
            addedPackages={addedPackages}
            centerPackages={centerPackages}
            selectedOfferingIds={selectedOfferingIds}
            selectedPackageIds={selectedPackageIds}
            error={error}
            setSelectedOfferingIds={setSelectedOfferingIds}
            setSelectedPackageIds={setSelectedPackageIds}
            setError={setError}
            onSelect={onSelectAddOn}
            onDeselect={onDeselectAddOn}
            onQuantityChange={onAddOnQuantityChange}
          />
        );
      })}
    </Grid>
  );
};

export default SuggestedAddOnList;
